/**
 * useCaptchaContext Hook
 *
 * Hook to access CAPTCHA context from CaptchaProvider.
 * Must be used within a CaptchaProvider.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

import { useContext } from 'react';
import { CaptchaContext } from './CaptchaProvider';
import type { CaptchaContextValue } from './types';

/**
 * Hook to access CAPTCHA context
 *
 * Provides access to:
 * - state: Current CAPTCHA state ('idle' | 'loading' | 'verifying' | 'challenged' | 'completed' | 'error')
 * - error: Current error if state is 'error'
 * - showChallenge: Function to trigger a CAPTCHA challenge
 * - reset: Function to reset CAPTCHA state
 *
 * @throws Error if used outside of CaptchaProvider
 *
 * @example
 * ```tsx
 * const { showChallenge, state } = useCaptchaContext();
 *
 * const handleLogin = async () => {
 *   const response = await api.login(credentials);
 *   if (response.captchaRequired) {
 *     const token = await showChallenge(response);
 *     // Retry with token
 *   }
 * };
 * ```
 */
export function useCaptchaContext(): CaptchaContextValue {
  const context = useContext(CaptchaContext);

  if (!context) {
    throw new Error(
      'useCaptchaContext must be used within a CaptchaProvider. ' +
        'Make sure your component is wrapped with <CaptchaProvider>.'
    );
  }

  return context;
}

/**
 * Alias for useCaptchaContext
 * For users who prefer the shorter name
 */
export const useCaptcha = useCaptchaContext;

export default useCaptchaContext;
